import React from 'react'
import { Button } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import Checkbox from '../../../../../../components/p5/Checkbox'
import DeleteIcon from '../../../../../../components/icons/DeleteIcon'
import EditIcon from '../../../../../../components/icons/EditIcon'
import CopyIcon from '../../../../../../components/icons/CopyIcon'
import { ConsignmentItem } from '../../../../consignment-item/models/types'
import { ConsignmentItemPatch } from '../../../../consignment-item/models/request'
import useConsignmentItemInlineEdit, { InlineEditField } from '../../../../consignment-item/components/useConsignmentItemInlineEdit'
import ConsignmentItemInlineEditCell from '../../../../consignment-item/components/ConsignmentItemInlineEditCell'
import ConsignmentItemPackagesCell from '../../../../consignment-item/components/ConsignmentItemPackagesCell'
import ConsignmentItemCommodityCodeCell from '../../../../consignment-item/components/ConsignmentItemCommodityCodeCell'

interface TransitConsignmentItemTableRowProps {
  rowData: ConsignmentItem
  rowIndex: number
  country: string | null
  onRowEdit: () => void
  onRowSave: (patch: ConsignmentItemPatch) => Promise<void>
  onRowRemove: () => void
  onRowDuplicate: () => void
  onRowSelect: () => void
  selected: boolean
  editDisabled: boolean
}

function TransitConsignmentItemTableRow({
  rowData,
  rowIndex,
  country,
  onRowEdit,
  onRowSave,
  onRowRemove,
  onRowDuplicate,
  onRowSelect,
  selected,
  editDisabled,
} : TransitConsignmentItemTableRowProps) {
  const { t } = useTranslation()
  const {
    editingField, isEditing, isSaving, values, draft, startEditing, cancelEditing, saveEditing, saveCommodityCode, setDraft,
  } = useConsignmentItemInlineEdit(rowData, onRowSave)

  const fieldEditDisabled = (field: InlineEditField) => (
    editDisabled || (editingField !== null && editingField !== field)
  )

  const rowClassName = [
    rowData.invalidCommodityCode ? 'invalid-row' : '',
    selected ? 'row-selected' : '',
    isEditing ? 'row-editing' : '',
  ].filter(Boolean).join(' ')

  return (
    <tr className={rowClassName}>
      <td className="w-1 align-middle">
        <Checkbox
          checked={selected}
          onChange={onRowSelect}
          wrapperClassName="d-flex align-items-center justify-content-center"
          inputClassName="form-check-input m-0 float-none"
        />
      </td>
      <td className="sequence-nr">{rowIndex + 1}</td>
      <td className="truncate-cell">
        <ConsignmentItemInlineEditCell
          isEditing={editingField === 'commodityDescriptionOfGoods'}
          draft={draft}
          isSaving={isSaving}
          editDisabled={fieldEditDisabled('commodityDescriptionOfGoods')}
          label={t('declaration.p5.descriptionOfGoods')}
          type="text"
          onStartEditing={() => startEditing('commodityDescriptionOfGoods')}
          onDraftChange={setDraft}
          onSave={saveEditing}
          onCancel={cancelEditing}
        >
          <span className="truncate-inner" title={values.commodityDescriptionOfGoods}>
            {values.commodityDescriptionOfGoods}
          </span>
        </ConsignmentItemInlineEditCell>
      </td>
      <td className="commodity-code-col">
        <ConsignmentItemCommodityCodeCell
          isEditing={editingField === 'commodityCode'}
          isSaving={isSaving}
          editDisabled={fieldEditDisabled('commodityCode')}
          country={country}
          isEns={false}
          harmonizedSystemSubHeadingCode={values.commodityHarmonizedSystemSubHeadingCode}
          combinedNomenclatureCode={values.commodityCombinedNomenclatureCode}
          onStartEditing={() => startEditing('commodityCode')}
          onSelect={saveCommodityCode}
          onCancel={cancelEditing}
        />
      </td>
      <td>
        <ConsignmentItemPackagesCell
          totalNumberOfPackages={rowData.totalNumberOfPackages}
          packagingCount={rowData.packagingCount}
          isEditing={editingField === 'totalNumberOfPackages'}
          draft={draft}
          isSaving={isSaving}
          editDisabled={fieldEditDisabled('totalNumberOfPackages')}
          onStartEditing={() => startEditing('totalNumberOfPackages')}
          onDraftChange={setDraft}
          onSave={saveEditing}
          onCancel={cancelEditing}
        />
      </td>
      <td>
        <ConsignmentItemInlineEditCell
          isEditing={editingField === 'goodsMeasureGrossMass'}
          draft={draft}
          isSaving={isSaving}
          editDisabled={fieldEditDisabled('goodsMeasureGrossMass')}
          label={t('declaration.p5.grossMass')}
          type="number"
          onStartEditing={() => startEditing('goodsMeasureGrossMass')}
          onDraftChange={setDraft}
          onSave={saveEditing}
          onCancel={cancelEditing}
        >
          {values.goodsMeasureGrossMass}
        </ConsignmentItemInlineEditCell>
      </td>
      <td>
        <ConsignmentItemInlineEditCell
          isEditing={editingField === 'goodsMeasureNetMass'}
          draft={draft}
          isSaving={isSaving}
          editDisabled={fieldEditDisabled('goodsMeasureNetMass')}
          label={t('declaration.p5.netMass')}
          type="number"
          onStartEditing={() => startEditing('goodsMeasureNetMass')}
          onDraftChange={setDraft}
          onSave={saveEditing}
          onCancel={cancelEditing}
        >
          {values.goodsMeasureNetMass}
        </ConsignmentItemInlineEditCell>
      </td>
      <td className="text-end">
        <div className="d-flex align-items-center justify-content-end gap-2">
          <Button
            type="button"
            variant="link"
            className="btn-icon"
            onClick={onRowDuplicate}
            disabled={editDisabled}
          >
            <CopyIcon />
          </Button>
          <Button
            type="button"
            variant="link"
            className="btn-icon"
            onClick={onRowEdit}
            disabled={editDisabled}
          >
            <EditIcon />
          </Button>
          <Button
            type="button"
            variant="link"
            className="btn-trash"
            onClick={onRowRemove}
            disabled={editDisabled}
          >
            <DeleteIcon />
          </Button>
        </div>
      </td>
    </tr>
  )
}

export default TransitConsignmentItemTableRow
